import React, { useState } from "react";
import {
  Box,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import FocusedFlowPage from "../../../components/FocusedFlowPage";
import { processSample } from "../../../utils/apiBiospecimen";
import { BIOBANK_ROUTES, BIOBANK_SUB_ROUTES } from "../../../constants/routes";

const AliquotDetails = () => {
  const { barcode } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();
  let storedFlow = {};
  try {
    storedFlow = JSON.parse(sessionStorage.getItem("aliquotFlow")) || {};
  } catch {
    storedFlow = {};
  }
  const returnTo = state?.returnTo || storedFlow.returnTo || BIOBANK_ROUTES.SAMPLE_PROCESSING;

  const [totalQty, setTotalQty] = useState("");
  const [completeSamples, setCompleteSamples] = useState("");
  const [comments, setComments] = useState("");
  const [failureReason, setFailureReason] = useState("");
  const [processedCount, setProcessedCount] = useState("");
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const isPositiveInt = (v) => {
    const n = Number(String(v).trim());
    return Number.isInteger(n) && n > 0;
  };

  const isNonNegativeInt = (v) => {
    const n = Number(String(v).trim());
    return Number.isInteger(n) && n >= 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();

    setSubmitError("");
    const nextErrors = {};
    const aliquotCount = Number(totalQty);
    if (!isPositiveInt(totalQty)) nextErrors.totalQty = true;
    if (!completeSamples) nextErrors.completeSamples = true;

    if (completeSamples === "No") {
      if (!failureReason.trim()) nextErrors.failureReason = true;
      if (
        !isNonNegativeInt(processedCount) ||
        (isPositiveInt(totalQty) && Number(processedCount) > aliquotCount)
      ) {
        nextErrors.processedCount = true;
      }
    }

    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    const processedAll = completeSamples === "Yes";
    const processedAliquots =
      completeSamples === "Yes" ? aliquotCount : Number(processedCount);

    const aliquotComments =
      completeSamples === "Yes" ? (comments && comments.trim()) || null : null;

    const sampleComments =
      completeSamples === "No" ? failureReason.trim() : null;

    const detailsForm = {
      completeSamples,
      comments: comments.trim(),
      failureReason: failureReason.trim(),
      processedCount: processedCount ? Number(processedCount) : "",

      processedAll,
      processedAliquots,
      aliquotComments,
      sampleComments,
    };
    let sessionFlow = {};
    try {
      sessionFlow = JSON.parse(sessionStorage.getItem("aliquotFlow")) || {};
    } catch {
      sessionFlow = {};
    }

    const ctx = {
      ...sessionFlow,
      ...(state || {}),
      sampleId: barcode,
      returnTo,
      aliquotCount,
      detailsForm,
    };
    sessionStorage.setItem("aliquotFlow", JSON.stringify(ctx));

    try {
      setSubmitting(true);

      await processSample(barcode, {
        totalAliquots: aliquotCount,
        processedAll,
        processedAliquots,
        aliquotComments,
        sampleComments,
      });
    } catch (err) {
      setSubmitError(err?.message || "Failed to save aliquot details.");
      return;
    } finally {
      setSubmitting(false);
    }

    navigate(BIOBANK_SUB_ROUTES.SAMPLE_PROCESSING_BARCODE_ASSIGNMENT(encodeURIComponent(barcode), aliquotCount), {
      state: ctx,
    });
  };

  return (
    <FocusedFlowPage
      title="Aliquot Details"
      subtitle="Please provide the following information about the Sample processing."
      onClose={() => navigate(returnTo)}
      maxWidth={700}
      actions={[
        <Button
          key="back"
          variant="outlined"
          onClick={() => navigate(BIOBANK_SUB_ROUTES.SAMPLE_PROCESSING_VERIFY(encodeURIComponent(barcode)), {
            state: { ...(storedFlow || {}), ...(state || {}), returnTo },
          })}
          startIcon={<ArrowBackIcon />}
          sx={{
            borderColor: "#8C5A3C",
            color: "#8C5A3C",
            fontWeight: 700,
            textTransform: "none",
            px: 3.5,
            height: 40,
            "&:hover": { borderColor: "#8C5A3C", background: "#FBF6F3" },
          }}
          disabled={submitting}
        >
          Back
        </Button>,
        <Button
          key="submit"
          type="submit"
          form="aliquot-details-form"
          variant="contained"
          sx={{
            bgcolor: "#500000",
            color: "#fff",
            fontWeight: 700,
            textTransform: "none",
            px: 3.5,
            height: 40,
            "&:hover": { bgcolor: "#6a0000" },
          }}
          disabled={submitting}
        >
          {submitting ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={18} /> Saving…
            </Box>
          ) : (
            "Submit"
          )}
        </Button>,
      ]}
    >
        {submitError ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {submitError}
          </Alert>
        ) : null}

        <Box
          id="aliquot-details-form"
          component="form"
          onSubmit={handleSubmit}
          sx={{ display: "grid", rowGap: 2.5 }}
        >
          <TextField
            label="Total Aliquot Quantity"
            required
            type="number"
            inputProps={{ min: 1 }}
            value={totalQty}
            onChange={(e) => setTotalQty(e.target.value)}
            size="small"
            sx={{ width: { xs: "100%", sm: 560 } }}
            error={!!errors.totalQty}
            helperText={errors.totalQty ? "Enter a positive whole number." : ""}
          />

          <FormControl
            size="small"
            required
            fullWidth
            error={!!errors.completeSamples}
          >
            <InputLabel>Were you able to process complete samples?</InputLabel>
            <Select
              value={completeSamples}
              label="Were you able to process complete samples?"
              onChange={(e) => setCompleteSamples(e.target.value)}
            >
              <MenuItem value={"Yes"}>Yes</MenuItem>
              <MenuItem value={"No"}>No</MenuItem>
            </Select>
          </FormControl>

          {completeSamples === "Yes" && (
            <TextField
              label="Comments about the Aliquots (optional)"
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              size="small"
              fullWidth
              multiline
              minRows={4}
            />
          )}

          {completeSamples === "No" && (
            <>
              <TextField
                label="If not, please briefly describe what happened."
                required
                value={failureReason}
                onChange={(e) => setFailureReason(e.target.value)}
                size="small"
                fullWidth
                multiline
                minRows={4}
                error={!!errors.failureReason}
                helperText={
                  errors.failureReason ? "This field is required." : ""
                }
              />
              <TextField
                label="How many complete samples were processed from this sample?"
                required
                type="number"
                inputProps={{ min: 0 }}
                value={processedCount}
                onChange={(e) => setProcessedCount(e.target.value)}
                size="small"
                sx={{ width: { xs: "100%", sm: 560 } }}
                error={!!errors.processedCount}
                helperText={
                  errors.processedCount ? "Enter a whole number from 0 to the total aliquot quantity." : ""
                }
              />
            </>
          )}
        </Box>
    </FocusedFlowPage>
  );
};

export default AliquotDetails;
